import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiEye,
  FiKey,
  FiSearch,
  FiShield,
  FiShieldOff,
} from "react-icons/fi";
import useAuth from "../../../hooks/useAuth";
import { actsAsAgencyAdmin } from "../../../utils/permissions";
import { repoUserService } from "../../../services/repoUser.service";
import repoAdminService from "../../../services/repoAdmin.service";
import { formatVehicleNumberDisplay } from "../../../utils/vehicleNumberUtils";
import DataTableToolbar from "../../../components/common/DataTableToolbar";
import StatusFilterSelect from "../../../components/common/StatusFilterSelect";
import PaginationBar from "../../../components/common/PaginationBar";
import { PAGE_LIMIT } from "../../../constants/pagination";
import { readListItems, readPagination } from "../../../utils/listPagination";
import { saveExcelBlob, getApiErrorMessage } from "../../../utils/downloadExcel";
import { printTablePage } from "../../../utils/printTable";
import { formatRepoRole } from "../../../constants/repoRoles";
import ResetPasswordModal from "../../../components/common/ResetPasswordModal";
import BlockCompanyModal from "../../../components/common/BlockCompanyModal";
import { loadRazorpayScript, openRazorpayCheckout } from "../../../utils/razorpayCheckout";
import "../../../styles/users.css";

export default function Users() {
  const { auth } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 0, total: 0 });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [pendingIds, setPendingIds] = useState(() => new Set());
  const [exportingUsers, setExportingUsers] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetError, setResetError] = useState("");
  const [resetFlash, setResetFlash] = useState("");
  const [companyInfo, setCompanyInfo] = useState(null);
  const [billing, setBilling] = useState(null);
  const [blockTarget, setBlockTarget] = useState(null);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, statusFilter]);

  const loadUsers = useCallback(
    async ({ silent = false } = {}) => {
      const showFullLoader = !silent && isFirstLoad.current;

      if (!auth?.token) {
        if (showFullLoader) setLoading(false);
        return;
      }

      try {
        if (showFullLoader) setLoading(true);
        if (!silent) setError("");

        const params = { page, limit: PAGE_LIMIT };
        if (debouncedQuery.trim()) params.q = debouncedQuery.trim();
        if (statusFilter && statusFilter !== "all") params.status = statusFilter;

        const res = await repoUserService.getUsers(auth.token, params);
        const list = readListItems(res);
        setUsers(Array.isArray(list) ? list : []);
        setPagination(readPagination(res));
      } catch (err) {
        if (!silent) {
          setError(err?.response?.data?.message || "Failed to load users");
          setUsers([]);
        }
      } finally {
        if (showFullLoader) {
          setLoading(false);
          isFirstLoad.current = false;
        }
      }
    },
    [auth?.token, debouncedQuery, statusFilter, page]
  );

  const isRepoAdmin = actsAsAgencyAdmin(auth);

  const loadBilling = useCallback(async () => {
    if (!auth?.token || !isRepoAdmin) return;
    try {
      const res = await repoUserService.getConnectEligibility(auth.token);
      setBilling(res?.data || res || null);
    } catch {
      setBilling(null);
    }
  }, [auth?.token, isRepoAdmin]);

  useEffect(() => {
    loadBilling();
  }, [loadBilling]);

  useEffect(() => {
    loadUsers({ silent: !isFirstLoad.current });
  }, [loadUsers]);

  useEffect(() => {
    if (!auth?.token || !isRepoAdmin) return;

    repoAdminService
      .getMyCompany(auth.token)
      .then((res) => {
        setCompanyInfo(res?.data?.company || res?.company || null);
      })
      .catch(() => setCompanyInfo(null));
  }, [auth?.token, isRepoAdmin]);

  const agentRegisterUrl = companyInfo?.companyCode
    ? `${window.location.origin}/agent-register?code=${encodeURIComponent(
        companyInfo.companyCode
      )}`
    : "";

  const handleCopyAgentLink = async () => {
    if (!agentRegisterUrl) return;
    try {
      await navigator.clipboard.writeText(agentRegisterUrl);
      alert("Agent registration link copied.");
    } catch {
      alert(agentRegisterUrl);
    }
  };

  const payAndActivateUser = async (user) => {
    const userId = user._id || user.id;
    await loadRazorpayScript();
    const orderRes = await repoUserService.createUserActivationOrder(
      userId,
      auth.token
    );
    const data = orderRes?.data || {};
    const checkout = await openRazorpayCheckout({
      key: data.razorpayKeyId,
      orderId: data.orderId,
      amount: data.amount,
      currency: data.currency,
      description: "User activation fee",
      prefill: {
        name: auth?.user?.name || "",
        email: auth?.user?.email || "",
        contact: auth?.user?.phone || "",
      },
    });
    await repoUserService.verifyUserActivationPayment(
      userId,
      {
        razorpay_order_id: checkout.razorpay_order_id,
        razorpay_payment_id: checkout.razorpay_payment_id,
        razorpay_signature: checkout.razorpay_signature,
        paymentId: data.paymentId,
      },
      auth.token
    );
    await Promise.all([loadUsers({ silent: true }), loadBilling()]);
  };

  const handleToggleStatus = async (user, blockReason = "") => {
    const userId = user._id || user.id;
    const nextStatus = user.isActive ? false : true;
    const previousStatus = user.isActive;

    setPendingIds((prev) => new Set(prev).add(userId));
    if (user.isActive) {
      setUsers((prev) =>
        prev.map((u) =>
          (u._id || u.id) === userId
            ? { ...u, isActive: false, blockedAt: new Date().toISOString(), blockReason }
            : u
        )
      );
    }

    try {
      if (nextStatus) {
        try {
          await repoUserService.changeUserStatus(userId, true, auth.token);
        } catch (err) {
          const needsPay =
            err?.response?.status === 402 ||
            err?.response?.data?.requiresPayment ||
            err?.response?.data?.code === "CONNECT_FEE_REQUIRED";
          if (!needsPay) throw err;
          await payAndActivateUser(user);
        }
        await Promise.all([loadUsers({ silent: true }), loadBilling()]);
      } else {
        await repoUserService.changeUserStatus(userId, false, auth.token, {
          blockReason: blockReason || "Blocked by admin",
        });
        await loadUsers({ silent: true });
      }
    } catch (err) {
      setUsers((prev) =>
        prev.map((u) =>
          (u._id || u.id) === userId ? { ...u, isActive: previousStatus } : u
        )
      );
      if (err?.message === "CHECKOUT_CLOSED") {
        alert("Payment was cancelled. User was not activated.");
      } else if (err?.message === "PAYMENT_FAILED") {
        alert("Payment failed. User was not activated.");
      } else {
        alert(err?.response?.data?.message || "Failed to update user status");
      }
    } finally {
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  const handleResetPassword = (user) => {
    setResetError("");
    setResetFlash("");
    setResetTarget(user);
  };

  const closeResetModal = () => {
    if (resetSubmitting) return;
    setResetTarget(null);
    setResetError("");
  };

  const confirmResetPassword = async (newPassword) => {
    if (!resetTarget) return;
    const userId = resetTarget._id || resetTarget.id;
    setResetSubmitting(true);
    setResetError("");
    try {
      await repoUserService.resetUserPassword(userId, newPassword, auth.token);
      setResetTarget(null);
      setResetFlash("Password reset successful");
    } catch (err) {
      setResetError(err?.response?.data?.message || "Failed to reset password");
    } finally {
      setResetSubmitting(false);
    }
  };

  const handleExportUsers = async () => {
    if (!auth?.token) return;

    try {
      setExportingUsers(true);
      const params = {};
      if (debouncedQuery.trim()) params.q = debouncedQuery.trim();
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;

      const response = await repoUserService.downloadUsersExcel(
        auth.token,
        params
      );
      await saveExcelBlob(response, "users.xlsx");
    } catch (err) {
      alert(await getApiErrorMessage(err, "Failed to download users Excel"));
    } finally {
      setExportingUsers(false);
    }
  };

  return (
    <div className="page companies-page">
      <div className="users-actions">
        <h2>All users</h2>
      </div>

      <div className="company-search-panel company-search-panel--simple">
        <div className="company-search-bar">
          <FiSearch className="company-search-icon" aria-hidden />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, email, phone, or role..."
            aria-label="Search users"
          />
        </div>
      </div>

      {error && <p className="error-text">{error}</p>}
      {resetFlash && (
        <p className="reset-password-flash" role="status">
          {resetFlash}
        </p>
      )}

      {isRepoAdmin && companyInfo?.companyCode && (
        <div className="agent-register-banner">
          <div>
            <strong>Agent self-registration</strong>
            <p className="muted" style={{ margin: "4px 0 0" }}>
              Share this link or company code{" "}
              <code>{companyInfo.companyCode}</code> so agents can register via
              the APK. New accounts stay inactive until you activate them.
            </p>
          </div>
          <button
            type="button"
            className="secondary-page-btn"
            onClick={handleCopyAgentLink}
          >
            Copy registration link
          </button>
        </div>
      )}

      <DataTableToolbar
        onDownloadExcel={handleExportUsers}
        onPrint={printTablePage}
        downloading={exportingUsers}
        downloadLabel="Download users (Excel)"
        leading={
          <StatusFilterSelect
            value={statusFilter}
            onChange={setStatusFilter}
            ariaLabel="Filter users by status"
          />
        }
      >
        {isRepoAdmin && (
          <Link to="/users/create">
            <button type="button" className="primary-page-btn">
              + Create User
            </button>
          </Link>
        )}
      </DataTableToolbar>

      {loading && users.length === 0 ? (
        <p>Loading users...</p>
      ) : (
        <div className="company-table-wrap printable-table-area">
          <h3 className="print-only-title">All users</h3>
          <table className="users-table excel-grid-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Vehicle number(s)</th>
                <th>Role</th>
                <th>Source</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="8">No users match your search or status filter.</td>
                </tr>
              ) : (
                users.map((user) => {
                  const userId = user._id || user.id;
                  const isAdmin = user.role === "REPO_ADMIN";
                  const isPending = pendingIds.has(userId);

                  return (
                    <tr key={userId} className={isPending ? "row-pending" : ""}>
                      <td>{user.name || "-"}</td>
                      <td>{user.email || "-"}</td>
                      <td>{user.phone || "-"}</td>
                      <td className="users-table-vehicles">
                        {Array.isArray(user.assignedVehicleNumbers) &&
                        user.assignedVehicleNumbers.length > 0
                          ? user.assignedVehicleNumbers
                              .map((v) => formatVehicleNumberDisplay(v))
                              .join(", ")
                          : "—"}
                      </td>
                      <td>{user.customRoleName || formatRepoRole(user.role)}</td>
                      <td>
                        {user.registrationSource === "SELF" ? "Self" : "Admin"}
                      </td>
                      <td>
                        <span
                          className={`company-status company-status--${
                            user.isActive
                              ? "active"
                              : user.blockedAt
                                ? "blocked"
                                : "inactive"
                          }`}
                        >
                          {user.isActive ? "Active" : user.blockedAt ? "Blocked" : "Inactive"}
                        </span>
                      </td>
                      <td className="company-actions-td">
                        <div className="company-actions-cell">
                          <Link
                            to={`/users/${userId}`}
                            className="company-btn company-btn--view"
                          >
                            <FiEye aria-hidden />
                            <span>View</span>
                          </Link>

                          {!isAdmin && isRepoAdmin && (
                            <>
                              <button
                                type="button"
                                className={`company-btn ${
                                  user.isActive
                                    ? "company-btn--block"
                                    : "company-btn--unblock"
                                }`}
                                disabled={isPending}
                                onClick={() => {
                                  if (user.isActive) setBlockTarget(user);
                                  else handleToggleStatus(user);
                                }}
                              >
                                {user.isActive ? (
                                  <FiShieldOff aria-hidden />
                                ) : (
                                  <FiShield aria-hidden />
                                )}
                                <span>
                                  {user.isActive
                                    ? "Block"
                                    : user.blockedAt
                                      ? "Unblock"
                                      : billing?.requiresActivationFee
                                        ? `Activate (₹${Number(
                                            billing.activationFeeAmount || 100
                                          ).toLocaleString("en-IN")})`
                                        : "Activate"}
                                </span>
                              </button>
                              <button
                                type="button"
                                className="company-btn company-btn--reset"
                                onClick={() => handleResetPassword(user)}
                              >
                                <FiKey aria-hidden />
                                <span>Reset Password</span>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
      <PaginationBar
        page={pagination.page || page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        loading={loading}
        onPageChange={setPage}
      />
      <ResetPasswordModal
        open={Boolean(resetTarget)}
        subjectName={resetTarget?.name}
        loading={resetSubmitting}
        error={resetError}
        onCancel={closeResetModal}
        onConfirm={confirmResetPassword}
      />
      <BlockCompanyModal
        open={Boolean(blockTarget)}
        companyName={blockTarget?.name}
        entityLabel="user"
        loading={blockTarget ? pendingIds.has(blockTarget._id || blockTarget.id) : false}
        onCancel={() => {
          if (blockTarget && pendingIds.has(blockTarget._id || blockTarget.id)) return;
          setBlockTarget(null);
        }}
        onConfirm={async (reason) => {
          const target = blockTarget;
          setBlockTarget(null);
          if (target) await handleToggleStatus(target, reason);
        }}
      />
    </div>
  );
}
